import React, { useState, useCallback, useMemo } from 'react';
import Button from './Button';
import Card from './Card';

/**
 * Wizard Step Indicator Component
 */
const StepIndicator = ({ steps, currentStep, onStepClick, allowClickBack = true }) => (
  <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2">
    {steps.map((step, index) => {
      const isCompleted = currentStep > index;
      const isCurrent = currentStep === index;
      const isClickable = allowClickBack && index < currentStep;

      return (
        <div key={step.id || index} className="flex items-center flex-shrink-0">
          <button
            type="button"
            onClick={() => isClickable && onStepClick?.(index)}
            disabled={!isClickable}
            className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
              ${isCurrent
                ? 'bg-blue-500 text-white'
                : isCompleted
                  ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }
              ${!isClickable && !isCurrent ? 'cursor-default' : ''}
            `}
          >
            {isCompleted ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              index + 1
            )}
          </button>
          <span
            className={`ml-2 text-xs hidden md:inline ${
              currentStep >= index
                ? 'text-gray-900 dark:text-gray-100'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {step.title}
          </span>
          {index < steps.length - 1 && (
            <div
              className={`w-8 h-0.5 mx-2 ${
                isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          )}
        </div>
      );
    })}
  </div>
);

/**
 * Progress Bar Component
 */
const ProgressBar = ({ current, total }) => {
  const percent = Math.round(((current + 1) / total) * 100);
  
  return (
    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-6">
      <div
        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};

/**
 * Generic Wizard Component
 * A reusable multi-step form wizard with validation support
 * 
 * @param {Object[]} steps - Array of step configurations
 * @param {string} steps[].id - Unique step identifier
 * @param {string} steps[].title - Step title for display
 * @param {React.ReactNode} steps[].component - Step content component
 * @param {string[]} steps[].fields - Field names to validate before proceeding (for react-hook-form)
 * @param {Function} onComplete - Called when wizard is completed with all form data
 * @param {Function} onStepChange - Called when step changes
 * @param {Object} form - Optional react-hook-form instance for validation
 * @param {string} title - Wizard title
 * @param {boolean} showProgressBar - Show progress bar
 * @param {boolean} showStepIndicator - Show step circles
 * @param {boolean} allowClickBack - Allow clicking on completed steps to go back
 */
const Wizard = ({
  steps,
  onComplete,
  onStepChange,
  form,
  title,
  showProgressBar = true,
  showStepIndicator = true,
  allowClickBack = true,
  submitText = 'Submit',
  nextText = 'Next',
  prevText = 'Back',
  cancelText = 'Cancel',
  onCancel,
  loading = false,
  className = '',
  children,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const currentStepConfig = steps[currentStep];

  // Handle step validation and navigation
  const handleNext = useCallback(async () => {
    // If form and fields are provided, validate before proceeding
    if (form && currentStepConfig.fields?.length > 0) {
      const isValid = await form.trigger(currentStepConfig.fields);
      if (!isValid) return;
    }

    if (isLastStep) {
      // Submit
      if (form) {
        form.handleSubmit(onComplete)();
      } else {
        onComplete?.();
      }
    } else {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep);
    }
  }, [currentStep, currentStepConfig, form, isLastStep, onComplete, onStepChange]);

  const handlePrev = useCallback(() => {
    if (!isFirstStep) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      onStepChange?.(prevStep);
    }
  }, [currentStep, isFirstStep, onStepChange]);

  const handleStepClick = useCallback((stepIndex) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      onStepChange?.(stepIndex);
    }
  }, [currentStep, onStepChange]);

  // Get current step content
  const stepContent = useMemo(() => {
    if (children) {
      // If children are provided, render the child at the current index
      const childArray = React.Children.toArray(children);
      return childArray[currentStep] || null;
    }
    return currentStepConfig?.component || null;
  }, [children, currentStep, currentStepConfig]);

  return (
    <Card className={className}>
      {/* Title */}
      {title && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {title}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Step {currentStep + 1} of {steps.length}: {currentStepConfig?.title}
          </p>
        </>
      )}

      {/* Progress Bar */}
      {showProgressBar && <ProgressBar current={currentStep} total={steps.length} />}

      {/* Step Indicator */}
      {showStepIndicator && (
        <StepIndicator
          steps={steps}
          currentStep={currentStep}
          onStepClick={handleStepClick}
          allowClickBack={allowClickBack}
        />
      )}

      {/* Step Content */}
      <div className="mb-6">
        {stepContent}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div>
          {!isFirstStep && (
            <Button type="button" variant="secondary" onClick={handlePrev} disabled={loading}>
              {prevText}
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
              {cancelText}
            </Button>
          )}
          <Button
            type="button"
            variant={isLastStep ? 'success' : 'primary'}
            onClick={handleNext}
            loading={loading}
          >
            {isLastStep ? submitText : nextText}
          </Button>
        </div>
      </div>
    </Card>
  );
};

/**
 * Wizard.Step Component
 * Helper component for defining wizard steps as children
 */
Wizard.Step = ({ children }) => children;

export default Wizard;

